import * as React from "react"
import 'intersection-observer';
import { InView } from 'react-intersection-observer';
import { Action, IOWidget, mk_pair, mk_widget, Widget } from "widgets-for-react"
import { CMSState, Link, textBlockNestedData, mediaBlockNestedData, quoteBlockNestedData } from "../types.gen"
import { link, getClassName, convertReactNodeToString } from "../utils.gen"
import { getMockedBlockRenderer, renderBlock } from "../block_to_widget.gen";
import { List } from "immutable";
import { blockInviewHandler } from "../../../drupal/rendering/inview_utils.mod";
import { datalayerHandler } from "../utils/gtm.mod"
import { prefersReducedMotion } from "../../../public_site/utils/prefersReducedMotion";

export type ExpandBlockProps<CustomBlockData, extra_images> = {
  index: number
  title?: React.ReactNode
  intro?: string
  items: ExpandProps<CustomBlockData, extra_images>[]
  link?: Link
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type ExpandProps<CustomBlockData, extra_images> = {
  index: number
  key?: string
  title: string
  subtitle?: React.ReactNode
  text: string
  open: boolean
  useSVGIcons: boolean
  blocks?: List<textBlockNestedData<CustomBlockData, extra_images> | mediaBlockNestedData<extra_images> | quoteBlockNestedData<extra_images>>
}

export type ExpandBlockStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  intro_classname: string
  content_classname: string
  list_classname: string
  footer_classname: string
  clickable_classname: string
  expand_item_classname: string
  expand_details_classname: string
  expand_summary_classname: string
  expand_title_classname: string
  expand_subtitle_classname: string
  expand_icon_classname: string
  expand_text_classname: string
  icon_open_id: string
  icon_closed_id: string
}>

export function ExpandBlock<CustomBlockData extends { kind: CustomBlockData["kind"] }, extra_images>(
  props: ExpandBlockProps<CustomBlockData, extra_images>,
  options?: { style?: ExpandBlockStyle; key?: string }
): IOWidget<CMSState<CustomBlockData, extra_images, never>, Action<CMSState<CustomBlockData, extra_images, never>>> {
  return (s: CMSState<CustomBlockData, extra_images, never>) =>
    mk_widget({
      run: onDone => (
        <InView
          as="section"
          onChange={(inView) => blockInviewHandler('expand', inView, `io-${options && options.key ? options.key : props.index}`, getClassName(options, "inview_classname", "inview"))}
          triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
          threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
          id={`io-${options && options.key ? options.key : props.index}`}
          className={getClassName(options, "block_classname", "container")}
          key={options && options.key ? options.key : `block-${props.index}`}
        >
          <div className={getClassName(options, "inner_classname", "py-5")}>
            
            {(props.title || props.intro) && (
              <header className={getClassName(options, "header_classname", "")}>
                {props.title && <h2 className={getClassName(options, "title_classname", "mb-3")}>{props.title}</h2>}
                {props.intro && <p className={getClassName(options, "intro_classname", "")}>{props.intro}</p>}
              </header>
            )}

            <div className={getClassName(options, "content_classname", "")}>
              <ul className={getClassName(options, "list_classname", "accordion list-unstyled mb-3")}>
                {props.items.map((value, index) =>
                  Expand<CustomBlockData, extra_images>(
                    s,
                    {
                      index: props.index,
                      key: `${options && options.key ? options.key : props.index}-${index}`,
                      text: value.text,
                      open: value.open,
                      title: value.title,
                      subtitle: value.subtitle,
                      useSVGIcons: value.useSVGIcons,
                      blocks: value.blocks
                    },
                    props,
                    index,
                    options
                  ).run(action => {
                    let openNew = action(value.open)
                    onDone(s1 => {
                      let currentBlock = s1.blocks.get(props.index) as any
                      if (currentBlock.kind === "expandBlock") {
                        currentBlock = {
                          ...currentBlock,
                          value: mk_pair(
                            {
                              ...currentBlock.value.fst,
                              items: currentBlock.value.fst.items.map(
                                (e:any, i: number) => (i === index ? { ...e, open: openNew } : e)
                              ),
                            },
                            currentBlock.value.snd
                          ),
                        }
                      }
                      return { ...s1, blocks: s1.blocks.set(props.index, currentBlock) }
                    })
                  })
                )}
              </ul>
            </div>

            {props.link &&
              <footer className={getClassName(options, "footer_classname", "")}> 
                {link(props.link,
                      {classname: getClassName(options, "clickable_classname", "btn btn-link pl-0")},
                      props.link.title)}
              </footer>
            }
          </div>
        </InView>
      ),
    })
}

function Expand<CustomBlockData extends { kind: CustomBlockData["kind"] }, extra_images>(
  s: CMSState<CustomBlockData, extra_images, never>,
  props: ExpandProps<CustomBlockData, extra_images>,
  blockProps: ExpandBlockProps<CustomBlockData, extra_images>,
  index: number,
  options?: { style?: ExpandBlockStyle; key?: string }
): Widget<Action<boolean>> {
  return mk_widget({
    run: onDone => 
      <li 
        className={getClassName(options, "expand_item_classname", "card")} 
        key={`expand-${options && options.key ? options.key : props.index}-${index}`}
      >
        <details className={getClassName(options, 'expand_details_classname', '' )}>
          <summary
            onClick={(event) => {
              toggleExpandable(event);
              props.open || datalayerHandler('expand', props.title, 'Expandable', {
                kind: 'button',
                title: convertReactNodeToString(blockProps.title),
                label: props.title
              })
              onDone((s) => !s)
            }}
            className={getClassName(options, "expand_summary_classname", `card-header ${props.open ? "col--open" : "col--closed"} text-left border-0`)}
            aria-controls={`a11y-${props.key}`}
            aria-expanded={props.open}
            aria-label="Toggle item"
          >
            <p className={getClassName(options, "expand_title_classname", "btn btn-link m-0 p-0")}>{props.title}</p>
            {props.subtitle && (<p className={getClassName(options, "expand_subtitle_classname", "text-muted m-0")}>{props.subtitle}</p>)}
            {props.useSVGIcons && (
              <svg className={getClassName(options, "expand_icon_classname", "mr-3")} aria-hidden="true">
                <use xlinkHref={`${props.open ? getClassName(options, "icon_open_id", "#icon--chevron-up") : getClassName(options, "icon_closed_id", "#icon--chevron-down")}`}/>
              </svg>
            )}
          </summary>
          
          <div
            id={`a11y-${props.key}`}
            className={getClassName(options, "expand_text_classname", `card-body pb-1 ${props.open ? "" : "d-none"} text-left border-0`)}
            aria-hidden={!props.open}
          >
            <div dangerouslySetInnerHTML={{ __html: props.text }} />
            {props.blocks.map((block , index)=> renderBlock<CustomBlockData, extra_images, never>(block, getMockedBlockRenderer(), `${options && options.key ? options.key : props.index}-${index}`)(s).run(() => {}))}
          </div>
        </details>
    </li>
  })
}

// based on https://css-tricks.com/how-to-animate-the-details-element-using-waapi/

export function toggleExpandable (event: any) {
  // user has a prefers reduced motion setting. Exit and skip the entire animation
  if(prefersReducedMotion()) {
    return;
  }

  // identify the three DOM elements
  const trigger:HTMLElement = event.currentTarget as HTMLElement;
  const content:HTMLElement = trigger.nextSibling as HTMLElement;
  const container:HTMLElement = trigger.closest('details') as HTMLElement;

  // set default values for the flags
  let animation:any = null;
  let isClosing:boolean = false;
  let isExpanding:boolean = false;

  // set the values for easing and duration
  const duration:number = 500; 
  const easeShrink:string = 'cubic-bezier(0.165, 0.840, 0.440, 1.000)'; // ease out quart
  const easeExpand:string = 'cubic-bezier(0.550, 0.055, 0.675, 0.190)'; // ease in cubic
  const isOpen:boolean = container.hasAttribute('open');

  // exit if one of the three DOM elements can't be found
  if(!trigger || !container || !content) {
    return false;
  }

  // prevent default behavior, otherwise the animation won't be possible
  event.preventDefault();
  container.style.overflow = 'hidden';

  // walk through the flags. Maybe the expandable is still mid-animtion and was clicked again
  if(isClosing || !isOpen) {
    _open();
  }

  if(isExpanding || isOpen) {
    _shrink();
  }

  // open, shrink, and expand are the three main methods
  function _open () {
    container.style.height = `${container.offsetHeight}px`;
    
    window.requestAnimationFrame(() => {
      container.setAttribute('open', 'open');
      _expand();
    });
  }

  function _shrink () {
    isClosing = true;
    const startHeight = `${container.offsetHeight}px`;
    const endHeight = `${trigger.offsetHeight}px`;

    if(animation) {
      animation.cancel();
    }

    animation = container.animate({
      height: [ startHeight, endHeight ]
    }, {
      duration: duration,
      easing: easeShrink,
    });

    // onfinish and oncancel are native events
    animation.onfinish = () => {
      container.removeAttribute('open');
      _onAnimationFinish();
    }

    animation.oncancel = () => {
      isClosing = false;
    }
  }

  function _expand () {
    isExpanding = true;
    const startHeight = `${container.offsetHeight}px`;
    const endHeight = `${trigger.offsetHeight + content.offsetHeight}px`;

    if(animation) {
      animation.cancel();
    }

    animation = container.animate({
      height: [startHeight, endHeight],
    }, {
      duration: duration,
      easing: easeExpand,
    });

    container.setAttribute('open', '');
    animation.onfinish = () => {
      _onAnimationFinish();
    }      
  }

  // reset everything to what it would have been without the animation in between
  function _onAnimationFinish () {
    animation = null;
    isClosing = false;
    isExpanding = false;
    container.removeAttribute('style');
  }
}